import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, Download, X } from 'lucide-react';

interface Props {
  title: string;
  filename: string;
  fetchPdf: () => Promise<Blob>;
  onClose: () => void;
}

// Etichetta/e big bag come PDF A4 server-side (#133). Scarica il blob dall'endpoint
// (auth Bearer via apiClient), lo mostra in anteprima e permette stampa/scaricamento.
export default function StampaEtichettaModal({ title, filename, fetchPdf, onClose }: Props) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const blob = await fetchPdf();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err: unknown) {
        const data = (err as { response?: { data?: unknown } })?.response?.data;
        let msg = t('magazzino.etichetta.errore_pdf');
        if (data instanceof Blob) {
          try { msg = JSON.parse(await data.text()).detail ?? msg; } catch { /* keep default */ }
        }
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fetchPdf, t]);

  function stampa() {
    iframeRef.current?.contentWindow?.print();
  }

  function scarica() {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{title}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-hidden bg-stone-bg">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-ink-soft">{t('magazzino.etichetta.generazione_pdf')}</div>
          ) : error ? (
            <div className="h-full flex items-center justify-center p-6">
              <div className="px-4 py-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger text-center">{error}</div>
            </div>
          ) : (
            <iframe ref={iframeRef} src={url ?? undefined} title={t('magazzino.etichetta.anteprima')} className="w-full h-full border-0" />
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-stone-mid bg-stone-bg">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white">{t('magazzino.etichetta.chiudi')}</button>
          <button type="button" onClick={scarica} disabled={!url}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white disabled:opacity-50 flex items-center gap-1.5">
            <Download size={15} /> {t('magazzino.etichetta.scarica')}
          </button>
          <button type="button" onClick={stampa} disabled={!url}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-50 flex items-center gap-1.5">
            <Printer size={15} /> {t('magazzino.etichetta.stampa')}
          </button>
        </div>
      </div>
    </div>
  );
}
